import { AlertCircle } from '@untitled-ui/icons-react';
import classNames from 'classnames';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLoaderData, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button, Modal } from '@myhealth-raccoon/components';
import { isRequestError, useAsyncData } from '@myhealth-raccoon/services';

import { OutstandingBillingCheck } from '../booking-flow/OutstandingBillingCheck';
import { PreBookingInformation } from '../booking-flow/PreBookingInformation';
import { VerificationMethodModal } from '../booking-flow/VerificationMethodModal';
import { BackButton } from 'src/components/BackButton.tsx';
import { ExpressTelehealthPreScreening } from 'src/components/ExpressTelehealthPreScreening';
import { InlineModal } from 'src/components/InlineModal';
import { StickyBottomBar } from 'src/components/StickyBottomBar.tsx';
import { Availability, AvailabilityTimeslotPayload } from 'src/components/availability';
import { AfterHoursAvailability } from 'src/components/availability/AfterHoursAvailability';
import { BulkBilledTelehealthEligibility } from 'src/components/forms/BulkBilledTelehealthEligibility';
import { PersonalDetailsForm, PersonalDetailsFormValues } from 'src/components/forms/PersonalDetailsForm';
import {
    BookWithClinicForm,
    BookWithDoctorForm,
    BookingInformationForm,
    WidgetFormRef,
} from 'src/components/forms/widget';
import { useMessageListener, usePostMessage } from 'src/contexts/message';
import { useAlertWarning } from 'src/hooks/useAlertWarning';
import { useClinicSelector } from 'src/hooks/useAsyncSelector';
import { useDebounce } from 'src/hooks/useDebounce';
import { useOpenApp } from 'src/hooks/useOpenApp';
import { useServiceTypeFilters } from 'src/hooks/useServiceTypeFilters';
import { PartialBookingUserWithToken, VisitInfo, authService } from 'src/services/auth';
import { AppointmentType, FirstAvailableModel, availabilityService } from 'src/services/availability';
import { BookingServiceSource, BookingUserProfileModel, bookingService } from 'src/services/booking';
import { MedicareCardStatus } from 'src/services/patient';
import { SiteBaseModel } from 'src/services/site';
import { umbracoService } from 'src/services/umbraco';
import { UserWithSiteModel } from 'src/services/user';
import { CodeRequestType } from 'src/services/verificationCode';
import { useDispatch } from 'src/store';
import { setBookingServiceSource, setClinicId } from 'src/store/appointment';
import { resetPatientHasDetails } from 'src/store/bookingFlow';
import {
    ExpressTelehealthPurpose,
    expressTelehealthPurpose,
    isExpressTelehealth,
    isFaceToFace,
    newPatientRegex,
} from 'src/utils/appointmentTypes';
import { selectedDoctorIsDisplayOnly } from 'src/utils/doctor';
import { firstAvailableToNumber } from 'src/utils/firstAvailable';
import { innerHtml } from 'src/utils/html';
import { clinicName, doctorName } from 'src/utils/name';
import { ConflictType, PerformConflictChecks, requiresEligibilityCheck } from 'src/utils/validateAppointmentType';

type Props = {
    className?: string;
    isClinicPage?: boolean;
    isModal?: boolean;
    hasPatientToken?: boolean;
};

type BOOKING_STEP =
    | 'AVAILABILITY'
    | 'PERSONAL_DETAILS'
    | 'VERIFICATION'
    | 'PATIENT_NOT_FOUND'
    | 'OUTSTANDING_BALANCE'
    | 'EXPRESS_PRE_SCREENING'
    | 'PHI'
    | 'BB_ELIGIBILITY'
    // 'next step' will decide dynamically what the next modal to display is
    | 'NEXT_STEP';

export function BookingFlow({ className, isClinicPage, isModal }: Props) {
    const [doctorDisplayName, setDoctorDisplayName] = useState<string>();
    const [doctorIsDisplayOnly, setDoctorIsDisplayOnly] = useState<boolean>(false);

    const { alertCopy, closeAlert, isAlertOpen, openAlert } = useAlertWarning();

    const { site, doctor: doctorSites } = useLoaderData() as { site?: SiteBaseModel; doctor?: UserWithSiteModel[] };
    const widgetType = site ? 'site' : 'doctor';
    const [urlSearchParams] = useSearchParams();

    const referrer = urlSearchParams.get('referrer');
    const [bookingQuery, setValues] = useState<BookingInformationForm>();

    const postMessage = usePostMessage();

    const { data: umbracoSite } = useClinicSelector();
    const clinicId = site?.id ?? (bookingQuery?.siteId ? Number(bookingQuery.siteId) : doctorSites![0].siteId) ?? 0;

    const afterHoursEnabled = urlSearchParams.has('afterHours') && urlSearchParams.get('afterHours') === 'True';
    const preselectedServiceType = urlSearchParams.has('serviceType') ? urlSearchParams.get('serviceType') : '';

    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(setClinicId(clinicId));
    }, [clinicId, dispatch]);
    useEffect(() => {
        const bookingService =
            preselectedServiceType === 'express telehealth'
                ? BookingServiceSource.TelehealthNow
                : BookingServiceSource.MyhealthBooking;
        dispatch(setBookingServiceSource(bookingService));
    }, [preselectedServiceType, dispatch]);

    const formRef = useRef<WidgetFormRef>(null);

    const [appointmentTypeId, setAppointmentTypeId] = useState<string>();
    const [isAvailablityDisabled, setIsAvailablityDisabled] = useState(false);

    const [bookingStep, setBookingStep] = useState<BOOKING_STEP>('AVAILABILITY');

    const [selectedTimeslot, setSelectedTimeslot] = useState<AvailabilityTimeslotPayload>();
    const [personalDetailsForm, setPersonalDetailsForm] = useState<PersonalDetailsFormValues>();
    const [isVerified, setIsVerified] = useState(false);
    const [verificationInfo, setIsVerificationInfo] = useState<Omit<PartialBookingUserWithToken, 'tokenResponse'>>();
    const [didPassOutstandingBilling, setDidPassOutstandingBilling] = useState(false);
    const [billingEligibility, setBillingEligibility] = useState<{
        medicareStatus: MedicareCardStatus;
        hasOshc: boolean;
    }>();
    const [expressTelehealthScreened, setExpressTelehealthScreened] = useState<ExpressTelehealthPurpose>();
    const [medicareExcemptions, setMedicareExemptions] = useState<string[]>();

    const { data: appointmentTypes, ...getAppointmentTypes } = useAsyncData(
        {
            service: availabilityService,
            method: availabilityService.GetAppointmentTypes,
        },
        Number(clinicId)
    );

    const { data: doctors, ...getDoctors } = useAsyncData(
        {
            service: bookingService,
            method: bookingService.GetDoctorsAtSite,
        },
        Number(clinicId)
    );
    const {
        afterHoursAppointmentTypes,
        serviceType,
        setServiceType,
        specialAppointmentTypes,
        standardAppointmentTypes,
        currentSpecialAppointmentType,
    } = useServiceTypeFilters(
        umbracoSite?.clinicServiceTypes,
        appointmentTypes,
        urlSearchParams.get('defaultTab')?.trim().toLocaleUpperCase()
    );

    const initialValues: Partial<BookingInformationForm> | undefined = useMemo(() => {
        if (!appointmentTypes && !doctorSites) {
            return undefined;
        }

        const expressTelehealthAvailable = appointmentTypes?.some((apt) => isExpressTelehealth(apt)) ?? false;
        const urlAppointmentMode =
            preselectedServiceType === 'express telehealth' && expressTelehealthAvailable ? 'express telehealth' : null;

        if (widgetType === 'site') {
            const defaultAppointment = appointmentTypes.find((apt) => apt.isDefaultAppt) ?? appointmentTypes[0];
            const fallbackAppointmentMode =
                defaultAppointment && isFaceToFace(defaultAppointment) ? 'faceToFace' : 'telehealth';

            return {
                patientType: 'existing',
                appointmentMode: urlAppointmentMode ?? fallbackAppointmentMode,
                appointmentTypeId: defaultAppointment.appointmentTypeId,
                doctorId: 'any',
            };
        } else {
            const defaultSite =
                doctorSites!.find((s) => s.umbracoSiteId && s.umbracoSiteId.toString() === referrer) ?? doctorSites![0];
            return {
                siteId: defaultSite.siteId.toString(),
                appointmentMode: urlAppointmentMode ?? 'faceToFace',
                patientType: 'existing',
            };
        }
    }, [appointmentTypes, doctorSites, preselectedServiceType, referrer, widgetType]);

    const changeValues = useDebounce(
        useCallback((newValues: BookingInformationForm) => {
            setValues({ ...newValues });
        }, []),
        300
    );

    const onValidate = useCallback((valid: boolean) => {
        setIsAvailablityDisabled(!valid);
    }, []);

    useEffect(() => {
        // if the site changes, RESET EVERYTHING about the flow
        setPersonalDetailsForm(undefined);
        setIsVerified(false);
        setBillingEligibility(undefined);
        setDidPassOutstandingBilling(false);
        setBookingStep('AVAILABILITY');
    }, [umbracoSite]);

    const { openApp } = useOpenApp();

    useMessageListener(
        useCallback(
            (payload) => {
                if (typeof payload === 'object' && payload.message === 'myhealth-booking-ui-after-hours') {
                    setServiceType(payload.value ? 'After Hours' : 'General Care');
                }
            },
            [setServiceType]
        )
    );

    const appointmentType = useMemo(
        () => appointmentTypes?.find((apt) => apt.appointmentTypeId === bookingQuery?.appointmentTypeId),
        [appointmentTypes, bookingQuery?.appointmentTypeId]
    );

    useEffect(() => {
        if (getAppointmentTypes.error) {
            postMessage({
                message: 'myhealth-booking-ui-fatal',
                value: getAppointmentTypes.error.message || getAppointmentTypes.error.name,
            });
        }
        if (getDoctors.error) {
            postMessage({
                message: 'myhealth-booking-ui-fatal',
                value: getDoctors.error.message || getDoctors.error.name,
            });
        }
    }, [getAppointmentTypes.error, getDoctors.error, postMessage]);

    const authorizeBookingApp = useCallback(async (clinicId: number, isNewPatient: boolean) => {
        const { access_token } = await umbracoService.GetBookingToken();
        authService.Token = { jwt: access_token, token_type: 'umbraco' };
        await authService.ConnectBookingApp(clinicId, isNewPatient);
    }, []);

    const {
        data: firstAvailable,
        isLoading: isLoadingFirstAvailable,
        invoke,
    } = useAsyncData(
        {
            service: availabilityService,
            method: availabilityService.GetFirstAvailable,
            selector: useCallback(
                (f: FirstAvailableModel[]) =>
                    f?.toSorted((a, b) => firstAvailableToNumber(a) - firstAvailableToNumber(b)) ?? [],
                []
            ),
            suppressToast: true,
        },
        clinicId,
        appointmentTypeId ?? '',
        null
    );

    const assertValidAppointment = useCallback(
        (
            args: Partial<{
                personalDetailsForm: PersonalDetailsFormValues;
                visitInfo: VisitInfo;
                appointmentType: AppointmentType;
                doctor: BookingUserProfileModel;
            }> = {}
        ) => {
            const visitInfo = (args.visitInfo ?? verificationInfo)!;
            if (!(args.appointmentType ?? appointmentType)!.newPatient && visitInfo.isNewPatient) {
                toast.warning('Appointment type not available for new patients.');
                throw 'Appointment type not available for new patients';
            }
            if (
                (args.appointmentType ?? appointmentType)!.appointmentName.match(newPatientRegex) &&
                (args.appointmentType ?? appointmentType)!.newPatient &&
                !visitInfo.isNewPatient
            ) {
                toast.warning('Appointment type not available for existing patients.');
                throw 'Appointment type not available for existing patients';
            }
            const conflictResult = PerformConflictChecks({
                appointmentType: args.appointmentType || appointmentType,
                patientDob: (args.personalDetailsForm ?? personalDetailsForm)!.dateOfBirth,
                doctor: args.doctor || doctors.find((dr) => dr.userId === selectedTimeslot?.doctorId.toString()),
                isNewPatient: visitInfo.isNewPatient,
                lastF2FDate: visitInfo.lastF2FDate,
                lastSeenDid: visitInfo.lastSeenDid,
                bannedLevel: visitInfo.bannedLevel,
            });
            if (!conflictResult.valid) {
                switch (conflictResult.conflictId) {
                    case ConflictType.doctorOwnPatientsOnly:
                        openAlert({
                            title: 'This provider only accepts patients they have previously seen',
                            body: `Please contact the centre to make an appointment on <a className="text-primary" href="tel:${umbracoSite?.phoneNumber}">${umbracoSite?.phoneNumber}</a> or select another provider.`,
                        });
                        break;
                    case ConflictType.doctorNoNewPatient:
                        openAlert({
                            title: 'This provider does not accept new patients',
                            body: `Please contact the centre to make an appointment on <a className="text-primary" href="tel:${umbracoSite?.phoneNumber}">${umbracoSite?.phoneNumber}</a> or select another provider.`,
                        });
                        break;
                    case ConflictType.patientBanned:
                        if (visitInfo.bannedLevel?.includes('Level 1')) {
                            openAlert({
                                title: 'We are currently unable to schedule an appointment for you',
                                body: `Please call the practice on <a className="text-primary" href="tel:${umbracoSite?.phoneNumber}">${umbracoSite?.phoneNumber}</a> to speak with one of our friendly receptionists to book with your doctor.`,
                            });
                        } else if (visitInfo.bannedLevel?.includes('Level 2')) {
                            openAlert({
                                title: 'We are currently unable to schedule an appointment for you.',
                                body: 'Please consult <a href="https://www.healthdirect.gov.au" target="_top">www.healthdirect.gov.au</a> for an alternative practice in the area.',
                            });
                        }
                        break;
                    case ConflictType.lastVisitTooLongAgo:
                        openAlert({
                            title: 'Appointment type unavailable',
                            body: `This appointment type is for patients who have had a face-to-face appointment within the last ${appointmentType?.lastF2FVisitInMonths} months.`,
                        });
                        break;
                    case ConflictType.outOfAgeRange:
                    default:
                        openAlert({
                            title: 'Appointment type unavailable',
                            body: 'Your eligibility could not be verified for this appointment type. Only patients within a certain age range, or identified section of our patient base, are currently eligible for this specific appointment type.',
                        });
                        break;
                }
                throw conflictResult.conflictId;
            }
        },
        [
            appointmentType,
            doctors,
            openAlert,
            personalDetailsForm,
            selectedTimeslot?.doctorId,
            umbracoSite?.phoneNumber,
            verificationInfo,
        ]
    );

    const selectTimeslot = useCallback(async (appt: AvailabilityTimeslotPayload) => {
        setSelectedTimeslot(appt);
        setBookingStep('NEXT_STEP');
    }, []);

    const selectAfterHoursAppointmentType = useCallback((apptType: AppointmentType) => {
        // @ts-expect-error types strange
        setValues((currentValues) => ({
            ...currentValues,
            appointmentTypeId: apptType.appointmentTypeId,
        }));
    }, []);

    const submitPersonalDetails = useCallback(
        async (personalDetails: PersonalDetailsFormValues, asNewPatient?: boolean) => {
            try {
                await authorizeBookingApp(clinicId, asNewPatient ?? bookingQuery!.patientType === 'new');
                const { tokenResponse: _, ...verificationInfo } = await authService.Initial({
                    dob: moment(new Date(personalDetails.dateOfBirth)).format('DD-MM-YYYY'),
                    firstname: personalDetails.firstName.trim(),
                    surname: personalDetails.lastName.trim(),
                    mobile: parsePhoneNumberWithError(personalDetails.mobileNumber, 'AU')!
                        .formatNational()
                        .replace(/\s*/g, ''),
                    siteId: clinicId,
                });
                setPersonalDetailsForm(personalDetails);
                setIsVerificationInfo(verificationInfo);

                // consider this a new patient, disregard any previous validation
                setIsVerified(false);
                setDidPassOutstandingBilling(false);
                setBillingEligibility(undefined);
                dispatch(resetPatientHasDetails());
                setMedicareExemptions(undefined);

                setBookingStep('VERIFICATION');
            } catch (e) {
                if (isRequestError(e) && e.statusCode === 404) {
                    setBookingStep('PATIENT_NOT_FOUND');
                } else {
                    console.error(e);
                }
            }
        },
        [authorizeBookingApp, bookingQuery, clinicId, dispatch]
    );

    const onSubmitVerification = useCallback(async (code: string, type: CodeRequestType) => {
        await authService.VerifyCode(code, type);
        setIsVerified(true);
        setBookingStep('NEXT_STEP');
    }, []);

    const submitPhi = useCallback(async (medicareStatus: MedicareCardStatus, hasOshc: boolean) => {
        setBillingEligibility({ medicareStatus, hasOshc });
        setBookingStep('NEXT_STEP');
    }, []);

    const savedPatient = useMemo(() => {
        if (personalDetailsForm && verificationInfo && isVerified) {
            return {
                displayName: `${personalDetailsForm.firstName} ${personalDetailsForm.lastName}`,
                dob: personalDetailsForm.dateOfBirth,
                visitInfo: verificationInfo,
            };
        }
    }, [isVerified, personalDetailsForm, verificationInfo]);

    const submitExemptions = useCallback(async (_isEligible: boolean, exemptions: string[]) => {
        setMedicareExemptions(exemptions);
        setBookingStep('NEXT_STEP');
    }, []);

    const onEditDetails = useCallback(() => {
        setBookingStep('PHI');
    }, []);
    const onBookAsSomeoneElse = useCallback(() => {
        setBookingStep('PERSONAL_DETAILS');
    }, []);

    const handlePatientNotFoundAction = useCallback(async () => {
        await submitPersonalDetails(
            JSON.parse(sessionStorage.getItem('personalDetailsForm')!) as PersonalDetailsFormValues,
            true
        );
    }, [submitPersonalDetails]);

    useEffect(() => {
        // when appt type changes, reset medicare exemptions
        setMedicareExemptions(undefined);
    }, [appointmentType]);

    useEffect(() => {
        switch (bookingStep) {
            case 'AVAILABILITY':
                setSelectedTimeslot(undefined);
                return;
            case 'NEXT_STEP':
                if (!selectedTimeslot || !appointmentType) {
                    setBookingStep('AVAILABILITY');
                    return;
                }
                if (!personalDetailsForm || !verificationInfo || !isVerified) {
                    setBookingStep('PERSONAL_DETAILS');
                    return;
                }

                try {
                    assertValidAppointment();
                } catch (e) {
                    setBookingStep('AVAILABILITY');
                    if (
                        e === 'Appointment type not available for new patients' ||
                        e === 'Appointment type not available for existing patients'
                    ) {
                        formRef.current?.openAppointmentTypeSelection();
                    }
                    if (
                        typeof e === 'number' &&
                        [ConflictType.lastVisitTooLongAgo, ConflictType.outOfAgeRange].includes(e)
                    ) {
                        formRef.current?.openAppointmentTypeSelection();
                    }
                    return;
                }

                if (!didPassOutstandingBilling && umbracoSite?.blockUnpaidBooking) {
                    setBookingStep('OUTSTANDING_BALANCE');
                    return;
                }
                if (isExpressTelehealth(appointmentType)) {
                    // if patient has not seen the *correct* pre-screening window,
                    if (expressTelehealthScreened !== expressTelehealthPurpose(appointmentType)) {
                        setBookingStep('EXPRESS_PRE_SCREENING');
                        return;
                    }
                }
                if (!billingEligibility) {
                    setBookingStep('PHI');
                    return;
                }
                if (
                    isExpressTelehealth(appointmentType) &&
                    billingEligibility.medicareStatus !== MedicareCardStatus.Valid
                ) {
                    // EXPRESS TELEHEALTH REQUIRES MEDICARE CARD
                    openAlert({
                        title: 'Appointment type unavailable',
                        body: `Express Telehealth is only available for eligible Medicare card holders. Please select another appointment type or contact the clinic to discuss your options.`,
                    });
                    setBookingStep('AVAILABILITY');
                    // Give the patient the opportunity to enter their details again
                    setBillingEligibility(undefined);
                    return;
                } else if (
                    !medicareExcemptions &&
                    requiresEligibilityCheck(appointmentType, verificationInfo, billingEligibility.medicareStatus)
                ) {
                    setBookingStep('BB_ELIGIBILITY');
                    return;
                }

                openApp(
                    clinicId,
                    selectedTimeslot.doctorId,
                    selectedTimeslot.date,
                    selectedTimeslot.appointmentTypeId,
                    {
                        name: `${personalDetailsForm.firstName} ${personalDetailsForm.lastName}`,
                        dob: personalDetailsForm.dateOfBirth,
                    },
                    selectedTimeslot.timeSlot,
                    verificationInfo,
                    billingEligibility.medicareStatus,
                    billingEligibility.hasOshc ? 'Overseas Student/Visitor Health Cover (OSHC / OVHC)' : undefined,
                    undefined,
                    medicareExcemptions
                );
                setBookingStep('AVAILABILITY');
                return;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingStep]);

    return (
        <div
            className={classNames(
                'flex w-full flex-col overflow-auto rounded-xl bg-white',
                { 'rounded-b-none sm:rounded-b-xl': isModal },
                className
            )}
        >
            <div className="sticky top-0 z-20 flex flex-col gap-2 rounded-t-xl border-b bg-white pb-2 pt-4 sm:pb-4">
                {isClinicPage && (
                    <div className="flex flex-row items-start gap-2 px-4">
                        <Button
                            variant="outline"
                            color={serviceType === 'General Care' ? 'primary' : 'gray'}
                            onClick={() => setServiceType('General Care')}
                        >
                            General Care
                        </Button>
                        {specialAppointmentTypes.map(([label]) => (
                            <Button
                                key={label}
                                variant="outline"
                                color={serviceType === label ? 'primary' : 'gray'}
                                onClick={() => setServiceType(label)}
                            >
                                {label}
                            </Button>
                        ))}
                        {umbracoSite?.isAfterHoursServiceAvailable && (
                            <Button
                                variant="outline"
                                color={serviceType === 'After Hours' ? 'primary' : 'gray'}
                                onClick={() => setServiceType('After Hours')}
                            >
                                After Hours
                            </Button>
                        )}
                    </div>
                )}
                {/* TITLE */}
                {serviceType !== 'After Hours' ? (
                    <h3 className="px-4">
                        <span className="block text-lg font-semibold text-gray-900">
                            Book with {doctorDisplayName ?? clinicName(site)}
                        </span>
                        <small className="mt-1 block text-sm font-light text-gray-600">
                            Your selections will help show the right availability.
                        </small>
                    </h3>
                ) : (
                    umbracoSite && (
                        <h3 className="px-4 text-lg font-semibold text-gray-900">
                            {umbracoSite.afterHoursServiceTitle || 'Book After-hours Care service'}
                        </h3>
                    )
                )}
            </div>
            {(serviceType === 'General Care' || currentSpecialAppointmentType) && (
                <div className="space-y-4 pb-5">
                    {widgetType === 'site' ? (
                        <BookWithClinicForm
                            className=""
                            doctors={doctors}
                            appointmentTypes={
                                currentSpecialAppointmentType
                                    ? currentSpecialAppointmentType[1]
                                    : standardAppointmentTypes
                            }
                            onNewValues={changeValues}
                            onValidate={onValidate}
                            defaultValues={initialValues}
                            firstAvailable={firstAvailable}
                            isLoadingFirstAvailable={isLoadingFirstAvailable}
                            isLoadingAppointmentTypes={getAppointmentTypes.isLoading}
                            isLoadingDoctors={getDoctors.isLoading}
                            onChangeAppointmentType={setAppointmentTypeId}
                            patient={savedPatient}
                            onEditDetails={onEditDetails}
                            onBookAsSomeoneElse={onBookAsSomeoneElse}
                            ref={formRef}
                        />
                    ) : (
                        <BookWithDoctorForm
                            clinics={doctorSites!}
                            onNewValues={changeValues}
                            onValidate={onValidate}
                            defaultValues={initialValues}
                            onDoctorDetails={(doc) => {
                                setDoctorDisplayName(doctorName(doc));
                                setDoctorIsDisplayOnly(doc.displayProfileNoOnlineBooking);
                            }}
                            onChangeAppointmentType={setAppointmentTypeId}
                            patient={savedPatient}
                            onEditDetails={onEditDetails}
                            onBookAsSomeoneElse={onBookAsSomeoneElse}
                            ref={formRef}
                        />
                    )}

                    {bookingQuery && appointmentType && bookingQuery.doctorId && <hr className="" />}

                    {bookingQuery &&
                        appointmentType &&
                        bookingQuery.doctorId &&
                        (bookingQuery.doctorId === 'any' ? (
                            <Availability
                                allUsers={doctors}
                                disabled={isAvailablityDisabled || bookingStep !== 'AVAILABILITY'}
                                appointmentType={appointmentType}
                                clinicId={Number(clinicId)}
                                onSelectTimeSlot={selectTimeslot}
                                futureApptWeeks={umbracoSite?.futureApptWeeks}
                                isLoadingFirstAvailable={isLoadingFirstAvailable}
                                firstAvailableDate={firstAvailable?.[0]?.firstAvailable?.date}
                                refreshAvailability={invoke}
                            />
                        ) : (
                            <Availability
                                errorElement={
                                    doctorIsDisplayOnly ||
                                    selectedDoctorIsDisplayOnly(bookingQuery.doctorId, doctors) ? (
                                        <>
                                            <p>
                                                <strong>This provider does not accept online bookings.</strong>
                                            </p>
                                            <p>
                                                Please select another provider or contact the centre to make an
                                                appointment on{' '}
                                                <a className="text-primary" href={`tel:${umbracoSite?.phoneNumber}`}>
                                                    {umbracoSite?.phoneNumber}
                                                </a>
                                                .
                                            </p>
                                        </>
                                    ) : undefined
                                }
                                disabled={
                                    isAvailablityDisabled ||
                                    selectedDoctorIsDisplayOnly(bookingQuery.doctorId, doctors) ||
                                    bookingStep !== 'AVAILABILITY'
                                }
                                doctorId={bookingQuery.doctorId}
                                appointmentType={appointmentType}
                                allUsers={doctors}
                                clinicId={Number(clinicId)}
                                onSelectTimeSlot={selectTimeslot}
                                futureApptWeeks={umbracoSite?.futureApptWeeks}
                                isLoadingFirstAvailable={isLoadingFirstAvailable}
                                firstAvailableDate={
                                    firstAvailable?.find((fa) => fa.user.userId.toString() === bookingQuery.doctorId)
                                        ?.firstAvailable?.date
                                }
                                refreshAvailability={invoke}
                            />
                        ))}
                </div>
            )}
            {serviceType === 'After Hours' &&
                umbracoSite &&
                (umbracoSite.isAfterHoursServiceAvailable ? (
                    <>
                        <div className="p-4 text-sm text-gray-700">
                            {!afterHoursEnabled && (
                                <span className="border-error-200 bg-error-50 text-error-700 mb-4 inline-block w-fit rounded-full px-2 py-0.5">
                                    <i className="bg-error-500 mr-2 inline-block size-1.5 rounded-full align-middle text-sm font-medium"></i>
                                    Currently unavailable while the clinic is open
                                </span>
                            )}

                            {umbracoSite.afterHoursServiceContentHtml ? (
                                <div
                                    className="prose prose-sm"
                                    {...innerHtml(umbracoSite.afterHoursServiceContentHtml)}
                                />
                            ) : (
                                <>
                                    <p className="mb-[1em]">
                                        <strong className="font-medium">
                                            Phone service for existing patients for 'Urgent' matters that can't wait
                                            untill regular clinic hours. It is a privately charged service with no
                                            Medicare rebate.
                                        </strong>
                                    </p>
                                    <p>
                                        For medical emergencies that require immediate medical attention, please
                                        consider your local Hospital Emergency Department or calling '000".
                                    </p>
                                </>
                            )}
                        </div>
                        {afterHoursEnabled && (
                            <>
                                <hr />
                                <h2 className="bg-gray-50 px-4 py-2.5 text-lg font-medium text-gray-900">
                                    Select a timeslot
                                </h2>
                                <AfterHoursAvailability
                                    onSelectAppointmentType={selectAfterHoursAppointmentType}
                                    siteId={clinicId}
                                    className="p-4"
                                    appointmentTypes={afterHoursAppointmentTypes}
                                    onSelectTimeSlot={selectTimeslot}
                                    allUsers={doctors}
                                />
                            </>
                        )}
                    </>
                ) : (
                    <div className="p-6 text-sm text-gray-700">
                        <h1 className="mb-1 text-lg font-semibold text-gray-900">
                            {umbracoSite.afterHoursServiceTitle || 'Book After-hours Care service'}
                        </h1>
                        <div
                            className="prose prose-sm"
                            {...innerHtml(umbracoSite.afterHoursServiceDisabledContentHtml)}
                        />
                    </div>
                ))}
            <InlineModal
                isOpen={bookingStep === 'PERSONAL_DETAILS'}
                onClose={() => setBookingStep('AVAILABILITY')}
                title={
                    <div className="text-sm font-normal text-gray-600">
                        <h2 className="mb-2 text-3xl font-semibold text-gray-900">Let's get you started</h2>
                        <p className="sm:mb-5">Please enter the details of the patient you are booking for.</p>
                        <p>
                            <span className="text-primary">*</span> indicates a required field
                        </p>
                    </div>
                }
            >
                <PersonalDetailsForm
                    phoneNumber={umbracoSite?.phoneNumber ?? ''}
                    onSubmit={submitPersonalDetails}
                    forceBlank={!!savedPatient}
                />
            </InlineModal>
            <InlineModal
                isOpen={bookingStep === 'PATIENT_NOT_FOUND'}
                onClose={() => setBookingStep('PERSONAL_DETAILS')}
                size="md"
                title={
                    <h2 className="text-3xl font-semibold text-gray-900">
                        We couldn't find a matching patient record.
                    </h2>
                }
            >
                <div className="prose prose-sm px-5 py-4">
                    <p>
                        We couldn’t find a match for the details you provided in our active patient records. Please
                        ensure that your <strong>name, date of birth, and mobile number</strong> match exactly as they
                        were entered during registration and try again.
                    </p>
                    <p>
                        If you don’t have an existing record, you can proceed as a <strong>new patient</strong>.
                        Alternatively, contact the clinic directly for assistance.
                    </p>
                </div>
                <StickyBottomBar className={'justify-end px-5'}>
                    <BackButton label={'Back'} onClick={() => setBookingStep('PERSONAL_DETAILS')} />
                    <Button color="primary" type="button" onClick={handlePatientNotFoundAction}>
                        Continue as New Patient
                    </Button>
                </StickyBottomBar>
            </InlineModal>
            <InlineModal
                isOpen={bookingStep === 'VERIFICATION'}
                onClose={() => setBookingStep('AVAILABILITY')}
                title={
                    <div className="text-sm font-normal text-gray-600">
                        <h2 className="mb-2 text-3xl font-semibold text-gray-900">Verify your identity</h2>
                        <p>
                            To verify your identity, we need you to select a verification method and enter the six-digit
                            code which expires in 5 minutes.
                        </p>
                    </div>
                }
            >
                <VerificationMethodModal
                    onSubmit={onSubmitVerification}
                    onBack={() => setBookingStep('PERSONAL_DETAILS')}
                    maskedEmail={verificationInfo?.email}
                    maskedMobile={verificationInfo?.mobile}
                />
            </InlineModal>
            <ExpressTelehealthPreScreening
                isOpen={bookingStep === 'EXPRESS_PRE_SCREENING'}
                appointmentType={appointmentType!}
                onAgree={(reason) => {
                    setExpressTelehealthScreened(reason);
                    setBookingStep('NEXT_STEP');
                }}
                onClose={() => setBookingStep('AVAILABILITY')}
            />
            <PreBookingInformation
                isOpen={bookingStep === 'PHI'}
                firstShown={!billingEligibility}
                onCancel={() => {
                    setBookingStep('AVAILABILITY');
                }}
                onContinue={submitPhi}
                isNewPatient={false}
                myMedicareRegisterPath={
                    site && site.automedSiteId
                        ? `${import.meta.env.VITE_AUTOMED_URL}/online/mymedicare/${site?.automedSiteId}/loc/${site.bpLocationId}`
                        : undefined
                }
            />
            {bookingStep === 'OUTSTANDING_BALANCE' && (
                <OutstandingBillingCheck
                    onCancel={() => setBookingStep('AVAILABILITY')}
                    onSuccess={() => {
                        setDidPassOutstandingBilling(true);
                        setBookingStep('NEXT_STEP');
                    }}
                />
            )}
            <BulkBilledTelehealthEligibility
                isOpen={bookingStep === 'BB_ELIGIBILITY'}
                visitInfo={verificationInfo}
                validMedicare={billingEligibility?.medicareStatus === MedicareCardStatus.Valid}
                onSubmit={submitExemptions}
                onClose={() => setBookingStep('AVAILABILITY')}
            />
            <Modal
                size="sm"
                open={isAlertOpen}
                onClose={closeAlert}
                icon={{ icon: AlertCircle, color: 'warning' }}
                panelClassname="!max-w-lg"
            >
                {alertCopy && (
                    <div className="prose">
                        <h4>{alertCopy.title}</h4>
                        <p {...innerHtml(alertCopy.body)} />
                    </div>
                )}
                <Button color="gray" className="mt-8 w-full" onClick={closeAlert}>
                    Close
                </Button>
            </Modal>
        </div>
    );
}
